/**
 * Decide QUAL viagem as telas internas mostram.
 *
 * Existe porque a viagem deixou de ser constante: uma conta participa de zero,
 * uma ou várias. A ordem das decisões é o que importa:
 *
 *   1. a lista chegou → a viagem escolhida, ou a primeira; nenhuma → "Minhas
 *      viagens";
 *   2. a lista ainda NÃO chegou, mas há uma escolhida guardada → abre nela;
 *   3. a lista falhou (sem rede) e há guardada → abre nela;
 *   4. nada disso → espera, e só manda para a lista quando há certeza.
 *
 * Os passos 2 e 4 juntos evitam os dois piores comportamentos: mandar para uma
 * lista vazia enquanto a resposta está a caminho (o que joga a pessoa fora do
 * roteiro ao abrir um link direto) e deixar a tela girando para sempre quando
 * a rede não volta.
 *
 * O que desce para as telas é a viagem **inteira**, não só o id: nome, destino,
 * datas e fuso são o que a Home mostra, e buscá-los outra vez lá dentro criaria
 * um intervalo em que a tela não sabe de qual viagem está falando.
 */
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { listTrips, type TripSummary } from '../auth/api';
import { useAuthStore } from '../auth/store';
import { Skeleton } from '../ui/primitives';
import { guardarViagemAtiva, lerViagemAtiva, type ViagemAtiva } from './viagemAtiva';
import { TripProvider } from './TripContext';

/** As duas formas são a mesma viagem; o servidor é apenas a mais fresca. */
function mesmaViagem(a: TripSummary, b: ViagemAtiva | null): boolean {
  return (
    b !== null &&
    a.id === b.id &&
    a.name === b.name &&
    a.primaryDestination === b.primaryDestination &&
    a.startDate === b.startDate &&
    a.endDate === b.endDate &&
    a.timezone === b.timezone &&
    a.baseCurrency === b.baseCurrency &&
    a.status === b.status &&
    a.role === b.role
  );
}

export function ViagemAtivaGate({ children }: { children: ReactNode }) {
  // A viagem guardada é lida COM a conta: sobra de outra conta no mesmo
  // aparelho nunca vira a viagem de quem acabou de entrar.
  const userId = useAuthStore((s) => s.user?.id ?? '');
  const guardada = lerViagemAtiva(userId);
  const viagens = useQuery({ queryKey: ['viagens'], queryFn: listTrips });

  if (viagens.isSuccess) {
    const lista = viagens.data.trips;
    const escolhida = lista.find((v) => v.id === guardada?.id) ?? lista[0];

    if (!escolhida) return <Navigate to="/viagens" replace />;

    if (!mesmaViagem(escolhida, guardada)) guardarViagemAtiva(userId, escolhida);

    return <TripProvider trip={escolhida}>{children}</TripProvider>;
  }

  // Sem resposta ainda (ou sem rede): o que está no aparelho vale. É o que faz
  // abrir a PWA offline continuar na viagem em que a pessoa estava.
  if (guardada) return <TripProvider trip={guardada}>{children}</TripProvider>;

  if (viagens.isError) return <Navigate to="/viagens" replace />;

  return (
    <section className="viagens">
      <Skeleton height={72} count={3} />
    </section>
  );
}
